import {
  BlocksViewGraph,
  BlocksViewLink,
  BlocksViewNode,
  BlocksViewVisualState,
} from '../types';

import { isGroup } from '../viewModel$/util';

import type { Bounds, Vector } from '@ardoq/graph';
import {
  rectEnclose,
  rectInterp,
  rectIntersects,
  vectorsInterp,
} from '../misc/geometry';
import { drawNode, hitTestNode } from './node/node';
import { drawLink, drawLinkLabel, getLinkLabelRect, hitTestLink } from './link';
import { clamp } from 'lodash';
import { easeOutCubic } from '../misc/math';
import { currentTimestamp } from '@ardoq/date-time';

import {
  BLOCKS_VIEW_LINK_LABEL_FONT,
  BLOCKS_VIEW_LINK_LINEMINWIDTH,
  BLOCKS_VIEW_LINK_LINEWIDTH,
  BLOCKS_VIEW_LOWLIGHT_ALPHA,
} from '../consts';
import type { CanvasRenderingContext } from './types';

export const hitTestGraphLinks = (
  graph: BlocksViewGraph,
  pos: Vector,
  ctx: CanvasRenderingContext
): BlocksViewLink | null => {
  const slop = 5 / ctx.getTransform().a;

  let ret = null;

  for (let i = 0; i < graph.edges.length && !ret; ++i) {
    ret = hitTestLink(graph.edges[i], pos, slop) ? graph.edges[i] : null;
  }

  return ret;
};

/**
 * The node with this graph id, which is not the same question as the component
 * it stands for: a grouping can render one component as several nodes, and only
 * the graph id says which of them is meant.
 *
 * Answers only for a node this view is drawing, on the same terms `renderNodes`
 * uses, because `bounds` means nothing otherwise: a node that has not been laid
 * out yet still carries `NODE_DEFAULTS.bounds`, and one under a closed group
 * keeps whatever it had before the collapse. Note that the descent, like the
 * renderer's, does not stop at a hidden group - only its own drawing does.
 */
export const findDrawnGraphNode = (
  node: BlocksViewNode,
  nodeId: string
): BlocksViewNode | null => {
  if (node.id === nodeId) {
    const isHidden = node.visualState & BlocksViewVisualState.Hidden;
    return node.cell && !isHidden ? node : null;
  }
  if (!isGroup(node) || !node.open) return null;
  for (const child of node.children) {
    const found = findDrawnGraphNode(child, nodeId);
    if (found) return found;
  }
  return null;
};

export const hitTestGraphNodes = (
  node: BlocksViewNode,
  pos: Vector
): BlocksViewNode | null => {
  const doit = (node: BlocksViewNode) => {
    let ret = hitTestNode(node, pos) ? node : null;

    if (isGroup(node) && node.open) {
      for (let i = 0; i < node.children.length && ret === node; ++i) {
        ret = doit(node.children[i]) ?? ret;
      }
    }

    return ret;
  };

  return doit(node);
};

const tween = easeOutCubic;

type GraphRenderMode = 'normal' | 'lowlight' | 'export';
export const renderGraph = (
  graph: BlocksViewGraph,
  window: Bounds,
  renderMode: GraphRenderMode,
  ctx: CanvasRenderingContext
) => {
  let done = true;

  const pixel = 1.0 / ctx.getTransform().a;
  ctx.globalAlpha = 1.0;

  const isLowlight = renderMode === 'lowlight';

  const renderNodes = (node: BlocksViewNode) => {
    const p = node.animation
      ? clamp(
          (currentTimestamp() - node.animation.begin) / node.animation.duration,
          0,
          1
        )
      : 1;

    if (node.animation && p === 1) {
      node.animation = null;
    }

    let cell = node.cell;

    if (node.cell && node.animation) {
      done = false;

      cell = rectInterp(
        tween(p),
        node.animation!.from as Bounds,
        node.cell,
        null
      );
    }

    const isHidden = node.visualState & BlocksViewVisualState.Hidden;

    if (cell && !isHidden && rectIntersects(cell, window)) {
      const alpha = isLowlight ? BLOCKS_VIEW_LOWLIGHT_ALPHA : 1.0;

      drawNode(node, cell, alpha, ctx);
    }

    if (isGroup(node) && node.open) {
      node.children?.forEach(child => renderNodes(child));
    }
  };

  const renderLinks = (links: BlocksViewLink[]) => {
    ctx.globalAlpha = 1.0;
    ctx.lineWidth = Math.max(
      BLOCKS_VIEW_LINK_LINEWIDTH,
      pixel * BLOCKS_VIEW_LINK_LINEMINWIDTH
    );

    for (const link of links) {
      if (link.route && link.bounds) {
        const p = link.animation
          ? clamp(
              (currentTimestamp() - link.animation.begin) /
                link.animation.duration,
              0,
              1
            )
          : 1;

        if (link.animation && p === 1) {
          link.animation = null;
        }

        let route = link.route;
        let bounds = link.bounds;

        if (link.animation) {
          done = false;

          route = vectorsInterp(
            tween(p),
            link.animation.from as Vector[],
            link.route,
            null
          );

          bounds = rectEnclose(...route);
        }

        if (!route || !bounds || !rectIntersects(window, bounds)) {
          continue;
        }

        drawLink(link, route, isLowlight, ctx);
      }
    }

    ctx.setLineDash([]);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = BLOCKS_VIEW_LINK_LABEL_FONT;

    const labelRects: Bounds[] = [];

    for (let i = 0; i < links.length; ++i) {
      const link = links[i];

      if (link.animation || !link.route || !link.bounds) {
        continue;
      }

      const rect = getLinkLabelRect(link, window, ctx);

      if (!rect) {
        continue;
      }

      let intersection = false;

      for (let j = 0; j < labelRects.length && !intersection; ++j) {
        intersection = rectIntersects(rect, labelRects[j]);
      }

      if (intersection) {
        continue;
      }

      drawLinkLabel(link, rect, isLowlight, ctx);
      labelRects.push(rect);
    }
  };

  renderNodes(graph.root);
  renderLinks(graph.edges);

  return done;
};
